import { createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import type { Permission } from '@voll-intelligence/voll-authz';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository } from '@web/data/repositories';
import { ViewAsPage } from '@web/ui/view_as';

import { orgRoute } from './org_routes';

/**
 * Guard for the "ver como viajante" screens. Reads `/me` and `/me/permissions`
 * from the cache primed by `orgRoute.loader`, then bounces to the org overview
 * when the caller lacks `permission` (both view-as grants exist only in the
 * VOLL org).
 */
export async function ensureOrgPermission(orgSlug: string, permission: Permission): Promise<void> {
  const me = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const org = me.orgs.find((o) => o.slug === orgSlug);
  if (!org) return redirect({ throw: true, to: '/' }) as never;

  const perms = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.permissions(org.id),
    queryFn: () => meRepository.getMyPermissions(org.id),
  });
  if (!perms.permissions.includes(permission)) {
    return redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } }) as never;
  }
}

export function ensureCanViewAs(orgSlug: string): Promise<void> {
  return ensureOrgPermission(orgSlug, 'kanban:traveler:view_as');
}

/**
 * `/orgs/$orgSlug/view-as` — the traveler's app in a phone frame (spec
 * 2026-10-12). A direct child of `orgRoute` like the traveler view: full
 * screen, no workspace shell. Search params carry only opaque ids; the
 * traveler's name rides in the history state (`{ travelerName }`), never in
 * the URL.
 */
export const viewAsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'view-as',
  validateSearch: z.object({
    customerId: z.string().optional(),
    cardId: z.string().optional(),
    cardSeq: z.string().regex(/^\d+$/).optional(),
  }),
  beforeLoad: ({ params }) => ensureCanViewAs(params.orgSlug),
  component: ViewAsPage,
});
